import { ReactNode, useState, createContext, useContext } from 'react'
import { ActiveCitation, ChatMessage, MessageStatus } from '../api'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppStateContext } from './AppProvider'

interface ChatContextType {
  messages: ChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  processMessages: MessageStatus
  setProcessMessages: React.Dispatch<React.SetStateAction<MessageStatus>>
  activeCitation: ActiveCitation | undefined
  setActiveCitation: React.Dispatch<React.SetStateAction<ActiveCitation | undefined>>
  isCitationPanelOpen: boolean
  setIsCitationPanelOpen: React.Dispatch<React.SetStateAction<boolean>>
  isIntentsPanelOpen: boolean
  setIsIntentsPanelOpen: React.Dispatch<React.SetStateAction<boolean>>
  startNewChat: () => void
  clearMessages: () => void,
  openQuickPrompts: () => void,
  isQuickPromptsOpen: boolean,
  setQuickPromptsOpen: React.Dispatch<React.SetStateAction<boolean>>,
}

interface ChatProviderProps {
  children: ReactNode
}

const ChatContext = createContext<ChatContextType>({
  messages: [],
  setMessages: () => { },
  processMessages: MessageStatus.NotRunning,
  setProcessMessages: () => { },
  activeCitation: undefined,
  setActiveCitation: () => { },
  isCitationPanelOpen: false,
  setIsCitationPanelOpen: () => { },
  isIntentsPanelOpen: false,
  setIsIntentsPanelOpen: () => { },
  startNewChat: () => { },
  clearMessages: () => { },
  openQuickPrompts: () => { },
  isQuickPromptsOpen: false,
  setQuickPromptsOpen: () => { },
})

const initialMessages: ChatMessage[] = [
  {
    content: 'Hello! I\'m SAMMY, your AI-driven assistant here to help you navigate the Spring and Annual Meetings. Feel free to ask me anything about the events,  topics, speakers or any details related to the meetings. \nHow can I assist you today?',
    date: new Date().toString(),
    id: '',
    role: 'assistant'
  },
]

export const ChatProvider = ({ children }: ChatProviderProps) => {
  const appStateContext = useContext(AppStateContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages)
  const [processMessages, setProcessMessages] = useState<MessageStatus>(MessageStatus.NotRunning)
  const [activeCitation, setActiveCitation] = useState<ActiveCitation | undefined>(undefined)
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false)
  const [isIntentsPanelOpen, setIsIntentsPanelOpen] = useState<boolean>(false)
  const [isQuickPromptsOpen, setQuickPromptsOpen] = useState<boolean>(false)

  const startNewChat = () => {
    setProcessMessages(MessageStatus.Processing)
    setMessages(initialMessages)
    setIsCitationPanelOpen(false)
    setIsIntentsPanelOpen(false)
    setActiveCitation(undefined)
    appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null })
    setProcessMessages(MessageStatus.Done)
    if (location.pathname !== '/') {
      navigate('/')
    }
  }

  const openQuickPrompts = () => {
    startNewChat();
    setQuickPromptsOpen(true);
  }

  const clearMessages = () => {
    setMessages(initialMessages)
  }

  const value = {
    messages,
    setMessages,
    processMessages,
    setProcessMessages,
    activeCitation,
    setActiveCitation,
    isCitationPanelOpen,
    setIsCitationPanelOpen,
    isIntentsPanelOpen,
    setIsIntentsPanelOpen,
    startNewChat,
    clearMessages,
    openQuickPrompts,
    isQuickPromptsOpen,
    setQuickPromptsOpen
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChatContext = () => useContext(ChatContext)
